import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from '../interfaces/api-response.interface';

export interface Company {
  id: number;
  name: string;
  type: string; // 'main' | 'fleet' | 'superadmin' | 'corporate' | 'button'
  status: string; // 'active' | 'blocked' | 'suspended'
  phone?: string;
  email: string;
  address?: string;
  city?: string;
  country_code?: string;
  vat_number?: string;
  registrationCode?: string;
  description?: string;
  parent_id?: number;
  logo?: string;
  corporateTariffId?: number | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyFilterDto {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  status?: string;
}

export interface CreateCompanyDto {
  name: string;
  type: string;
  status: string;
  email: string;
  description?: string;
  phone?: string;
  address?: string;
  city?: string;
  country_code?: string;
  vat_number?: string;
  registrationCode?: string;
  parent_id?: number;
  corporateTariffId?: number | null;
}

export interface UpdateCompanyDto {
  name?: string;
  type?: string;
  status?: string;
  email?: string;
  description?: string;
  phone?: string;
  address?: string;
  city?: string;
  country_code?: string;
  vat_number?: string;
  registrationCode?: string;
  parent_id?: number;
  corporateTariffId?: number | null;
}

export interface CompanySetting {
  id: number;
  company_id: number;
  key: string;
  value: string;
  created_at: string;
  updated_at: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private baseUrl = '/api/companies';

  constructor(private http: HttpClient) {}

  // CRUD Operations
  getCompanies(filter?: CompanyFilterDto): Observable<ApiResponse<Company[]>> {
    let params = new HttpParams();
    if (filter) {
      if (filter.page) params = params.set('page', filter.page.toString());
      if (filter.limit) params = params.set('limit', filter.limit.toString());
      if (filter.search) params = params.set('search', filter.search);
      if (filter.type) params = params.set('type', filter.type);
      if (filter.status) params = params.set('status', filter.status);
    }
    return this.http.get<ApiResponse<Company[]>>(this.baseUrl, { params });
  }

  getCompanyById(id: number): Observable<ApiResponse<Company>> {
    return this.http.get<ApiResponse<Company>>(`${this.baseUrl}/${id}`);
  }

  createCompany(data: CreateCompanyDto): Observable<ApiResponse<Company>> {
    return this.http.post<ApiResponse<Company>>(this.baseUrl, data);
  }

  updateCompany(id: number, data: UpdateCompanyDto): Observable<ApiResponse<Company>> {
    return this.http.put<ApiResponse<Company>>(`${this.baseUrl}/${id}`, data);
  }

  deleteCompany(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  blockCompany(id: number): Observable<ApiResponse<Company>> {
    return this.http.put<ApiResponse<Company>>(`${this.baseUrl}/${id}/block`, {});
  }

  unblockCompany(id: number): Observable<ApiResponse<Company>> {
    return this.http.put<ApiResponse<Company>>(`${this.baseUrl}/${id}/unblock`, {});
  }

  // Company Settings
  getCompanySettings(companyId: number): Observable<ApiResponse<Record<string, string>>> {
    return this.http.get<ApiResponse<Record<string, string>>>(`${this.baseUrl}/${companyId}/settings`);
  }

  getCompanySetting(companyId: number, key: string): Observable<ApiResponse<CompanySetting>> {
    return this.http.get<ApiResponse<CompanySetting>>(`${this.baseUrl}/${companyId}/settings/${key}`);
  }

  setCompanySetting(companyId: number, key: string, value: string): Observable<ApiResponse<CompanySetting>> {
    return this.http.post<ApiResponse<CompanySetting>>(`${this.baseUrl}/${companyId}/settings`, { key, value });
  }

  updateCompanySettings(companyId: number, settings: Array<{ key: string; value: string }>): Observable<ApiResponse<Record<string, string>>> {
    return this.http.put<ApiResponse<Record<string, string>>>(`${this.baseUrl}/${companyId}/settings/bulk`, { settings });
  }

  deleteCompanySetting(companyId: number, key: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${companyId}/settings/${key}`);
  }

  // Fleet Management
  getFleetCompanies(id: number): Observable<ApiResponse<Company[]>> {
    return this.http.get<ApiResponse<Company[]>>(`${this.baseUrl}/${id}/fleets`);
  }

  // Resources
  getCompanyDrivers(id: number): Observable<ApiResponse<unknown[]>> {
    return this.http.get<ApiResponse<unknown[]>>(`${this.baseUrl}/${id}/drivers`);
  }

  getCompanyCars(id: number): Observable<ApiResponse<unknown[]>> {
    return this.http.get<ApiResponse<unknown[]>>(`${this.baseUrl}/${id}/cars`);
  }

  getCompanyStats(id: number): Observable<ApiResponse<unknown>> {
    return this.http.get<ApiResponse<unknown>>(`${this.baseUrl}/${id}/stats`);
  }

  resetAdminPassword(companyId: number, newPassword: string): Observable<ApiResponse<void>> {
    return this.http.patch<ApiResponse<void>>(`${this.baseUrl}/${companyId}/reset-admin-password`, { newPassword });
  }

  impersonate(companyId: number): Observable<ApiResponse<{
    token: string;
    expiresAt: string;
    companyName: string;
    companyId: number;
    companyType: string;
    originalCompanyId: number;
  }>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/${companyId}/impersonate`, {});
  }
}
